import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  CheckCircle, AlertTriangle, XCircle, 
  ArrowRight, Target, Shield, TrendingUp,
  Sparkles, BookOpen
} from "lucide-react";

/**
 * Post-Action Feedback Screen
 * Shows SAFE/BORDERLINE/UNSAFE status after diagnostic tests
 */
const DiagnosticFeedbackPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  
  // Get data passed from diagnostic page
  const { 
    testType = "gs", 
    score = 0, 
    status = "borderline",
    correct = 0,
    total = 0,
    timeSpent = 0
  } = location.state || {};

  const [showDetails, setShowDetails] = useState(false);

  useEffect(() => {
    // Auto-show details after animation
    const timer = setTimeout(() => setShowDetails(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  // Status configurations
  const statusConfig = {
    safe: {
      icon: CheckCircle,
      color: "text-emerald-600",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-200",
      gradientFrom: "from-emerald-500",
      gradientTo: "to-green-600",
      badge: "🟢 SAFE",
      title: "You're in the Safe Zone!",
      subtitle: testType === "csat" 
        ? "Your CSAT is secured. Focus on GS now."
        : "Your GS foundation is solid. Keep building.",
      message: testType === "csat"
        ? "Your CSAT score indicates you're unlikely to be eliminated in Paper 2. This is a significant advantage - many aspirants fail here."
        : "Your GS accuracy shows strong conceptual clarity. Continue with regular practice to maintain this level.",
      nextAction: "Continue to Dashboard",
      nextPath: "/dashboard",
      tips: [
        "Maintain consistency with daily practice",
        "Don't neglect this area - revisit weekly",
        "Focus on other weaker areas now"
      ]
    },
    borderline: {
      icon: AlertTriangle,
      color: "text-amber-600",
      bgColor: "bg-amber-50",
      borderColor: "border-amber-200",
      gradientFrom: "from-amber-500",
      gradientTo: "to-orange-600",
      badge: "🟡 BORDERLINE",
      title: "You're on the Edge",
      subtitle: testType === "csat"
        ? "One bad day could eliminate you. Act now."
        : "Your GS needs strengthening before Prelims.",
      message: testType === "csat"
        ? "Your CSAT score is in the danger zone. In actual exam conditions with stress, you could slip below the cutoff. Dedicated practice is essential."
        : "Your GS accuracy is inconsistent. With focused practice on weak areas, you can move to the safe zone within 2-3 weeks.",
      nextAction: testType === "csat" ? "Start CSAT Practice" : "Practice GS MCQs",
      nextPath: testType === "csat" ? "/csat-suite" : "/prelims/mcq-practice",
      tips: [
        "Practice 20-30 questions daily in this area",
        "Focus on elimination technique",
        "Take timed mini-tests to build exam temperament"
      ]
    },
    unsafe: {
      icon: XCircle,
      color: "text-red-600",
      bgColor: "bg-red-50",
      borderColor: "border-red-200",
      gradientFrom: "from-red-500",
      gradientTo: "to-rose-600",
      badge: "🔴 UNSAFE",
      title: "Critical Attention Required",
      subtitle: testType === "csat"
        ? "Your entire attempt is at risk. Priority #1."
        : "Significant gaps detected. Intensive focus needed.",
      message: testType === "csat"
        ? "Your CSAT score puts your entire UPSC attempt at risk. No matter how well you do in GS, failing CSAT means elimination. This must be your top priority."
        : "Your GS accuracy indicates major conceptual gaps. Without immediate intervention, Prelims qualification is unlikely.",
      nextAction: testType === "csat" ? "Start CSAT Intensive" : "Start Foundation Building",
      nextPath: testType === "csat" ? "/csat-suite" : "/prelims/mcq-practice",
      tips: [
        "Dedicate 1-2 hours daily to this area",
        "Start with basics - don't skip fundamentals",
        "Track progress weekly with mock tests",
        "Consider this your #1 priority until safe"
      ]
    }
  };

  const config = statusConfig[status] || statusConfig.borderline;
  const StatusIcon = config.icon;

  // Calculate percentage
  const percentage = total > 0 ? Math.round((correct / total) * 100) : score;

  return (
    <div className={`min-h-screen bg-gradient-to-br ${config.gradientFrom} ${config.gradientTo}`}>
      <div className="max-w-lg mx-auto px-4 py-8">
        
        {/* Status Badge - Animated */}
        <div className="text-center mb-6 animate-fade-in">
          <div className={`inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/20 backdrop-blur-sm text-white text-lg font-bold shadow-lg`}>
            <StatusIcon className="w-6 h-6" />
            {config.badge}
          </div>
        </div>

        {/* Main Score Card */}
        <Card className="border-0 shadow-2xl mb-6 overflow-hidden">
          <CardContent className="p-0">
            {/* Score Section */}
            <div className={`${config.bgColor} p-6 text-center border-b ${config.borderColor}`}>
              <p className="text-sm text-slate-600 mb-2">
                {testType === "csat" ? "CSAT Diagnostic" : "GS Foundation Check"}
              </p>
              <div className={`text-6xl font-bold ${config.color} mb-2`}>
                {percentage}%
              </div>
              <p className="text-slate-600">
                {correct} correct out of {total} questions
              </p>
              {timeSpent > 0 && (
                <p className="text-sm text-slate-500 mt-1">
                  Time: {Math.floor(timeSpent / 60)}m {timeSpent % 60}s
                </p>
              )}
            </div>

            {/* Message Section */}
            <div className="p-6">
              <h2 className={`text-xl font-bold ${config.color} mb-2`}>
                {config.title}
              </h2>
              <p className="text-slate-600 mb-4">
                {config.subtitle}
              </p>
              
              {showDetails && (
                <div className="animate-fade-in">
                  <p className="text-sm text-slate-600 mb-4 leading-relaxed">
                    {config.message}
                  </p>

                  {/* Tips Section */}
                  <div className={`${config.bgColor} rounded-lg p-4 mb-4`}>
                    <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <Sparkles className="w-4 h-4" />
                      CLARIAS Recommendations
                    </p>
                    <ul className="space-y-2">
                      {config.tips.map((tip, idx) => (
                        <li key={idx} className="text-sm text-slate-600 flex items-start gap-2">
                          <span className={`${config.color} mt-1`}>•</span>
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* What This Means Section */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <p className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                      <BookOpen className="w-4 h-4" />
                      What This Means for Your Preparation
                    </p>
                    {status === "safe" && (
                      <p className="text-sm text-slate-600">
                        You can now focus your energy on other areas. But don't completely neglect {testType === "csat" ? "CSAT" : "GS"} - 
                        schedule a weekly revision to maintain your level.
                      </p>
                    )}
                    {status === "borderline" && (
                      <p className="text-sm text-slate-600">
                        You're not in immediate danger, but you're one bad day away from elimination. 
                        With 2-3 weeks of dedicated practice, you can move to the safe zone.
                      </p>
                    )}
                    {status === "unsafe" && (
                      <p className="text-sm text-slate-600">
                        This needs to become your priority. Without significant improvement here, 
                        other preparation efforts may go to waste. CLARIAS will guide you daily.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="space-y-3">
          <Button 
            onClick={() => navigate(config.nextPath)}
            className="w-full bg-white text-slate-800 hover:bg-white/90 h-14 text-lg font-semibold shadow-lg"
          >
            {config.nextAction}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
          
          <Button 
            onClick={() => navigate("/dashboard")}
            variant="ghost"
            className="w-full text-white/90 hover:text-white hover:bg-white/10"
          >
            Go to Dashboard
          </Button>
        </div>

        {/* Motivational Footer */}
        <div className="mt-8 text-center">
          <p className="text-white/80 text-sm">
            {status === "safe" && "🎯 Great start! Keep the momentum going."}
            {status === "borderline" && "💪 You have time to improve. Start today."}
            {status === "unsafe" && "🔥 Every topper started somewhere. Your journey begins now."}
          </p>
        </div>

      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fadeIn 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default DiagnosticFeedbackPage;
